"use client";

import { unwrapCountedObjects } from "@/components/ProjectsInArtwork/utils/artworkListApiTransform";
import { normalizeVendorAssessmentListItem } from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";
import type {
  TastingPanelListItem,
  TastingPanelListRequest,
  TastingPanelListResponse,
} from "../types/tastingPanelListApi.types";

export {
  formatVendorAssessmentStatusLabel as formatTastingPanelStatusLabel,
  resolveVendorPublished as resolveTastingPanelPublished,
  isVendorAssessmentStatusValue as isTastingPanelVendorAssessmentStatus,
} from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

export function normalizeTastingPanelListItem(
  raw: unknown,
  fallbackId: string
): TastingPanelListItem {
  return normalizeVendorAssessmentListItem(raw, fallbackId) as TastingPanelListItem;
}

export function normalizeTastingPanelListResponse(
  raw: unknown,
  req: TastingPanelListRequest,
  totalFromHeader?: unknown
): TastingPanelListResponse {
  if (Array.isArray(raw)) {
    const headerTotal = toNumber(totalFromHeader);
    return {
      items: raw.map((it, idx) => normalizeTastingPanelListItem(it, `${req.pageNo}-${idx}`)),
      totalCount: headerTotal ?? raw.length,
      pageNo: req.pageNo,
      pageSize: req.pageSize,
    };
  }

  const rawAsUnknown = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const itemsContainer =
    rawAsUnknown.items ??
    rawAsUnknown.data ??
    rawAsUnknown.content ??
    rawAsUnknown.objects ??
    [];
  const unwrappedItems = unwrapCountedObjects(itemsContainer);

  const headerTotal = toNumber(totalFromHeader);
  const bodyTotal =
    toNumber(rawAsUnknown.totalCount) ??
    toNumber(rawAsUnknown.totalElements) ??
    toNumber(rawAsUnknown.totalRecords) ??
    toNumber(rawAsUnknown.count);

  const normalizedItems = unwrappedItems.map((it, idx) =>
    normalizeTastingPanelListItem(it, `${req.pageNo}-${idx}`)
  );

  return {
    items: normalizedItems,
    totalCount: headerTotal ?? bodyTotal ?? normalizedItems.length,
    pageNo: toNumber(rawAsUnknown.pageNo) ?? toNumber(rawAsUnknown.pageNumber) ?? req.pageNo,
    pageSize: toNumber(rawAsUnknown.pageSize) ?? toNumber(rawAsUnknown.size) ?? req.pageSize,
  };
}
